import React from "react";
import { Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

/**
 * How the overview's roll cards bucket time. One page-level setting: the cards
 * sit above one another describing the same output, so a day on one and a week
 * on the next would invite comparing periods of different lengths.
 */
export type LamGrouping = "shift" | "day" | "week";

export const GROUP_OPTIONS: Array<{ key: LamGrouping; label: string }> = [
  // Shift is what the PLC actually counts; day and week are sums of it.
  { key: "shift", label: "Shift" },
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
];

/** "7 shifts" / "1 day" — a count of whichever bucket is on screen. */
export const groupCount = (n: number, group: LamGrouping) =>
  `${n} ${group}${n === 1 ? "" : "s"}`;

/**
 * Shift, because it is the grain the PLC actually counts at — a day or a week
 * is a sum somebody chose to make. Opening on the raw grain means the first
 * thing on screen is what the machine reported, not an aggregate of it.
 */
export const DEFAULT_GROUPING: LamGrouping = "shift";

/**
 * `onChange` only fires for a real change — the group is exclusive, so clicking
 * the selected button would otherwise report null and clear the selection.
 */
const GroupSelector: React.FC<{
  value: LamGrouping;
  onChange: (group: LamGrouping) => void;
}> = ({ value, onChange }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    {/* Named in front of the buttons, matching the line switch: "Shift / Day /
        Week" alone could be read as a filter down to one of them. */}
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
      aria-hidden
    >
      Group by:
    </Typography>
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value}
      onChange={(_, v: LamGrouping | null) => {
        if (v && v !== value) onChange(v);
      }}
      aria-label="Group by"
    >
      {GROUP_OPTIONS.map((g) => (
        <ToggleButton key={g.key} value={g.key}>
          {g.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  </Stack>
);

export default GroupSelector;
